import {
  MULTILINE_KPIS,
  MULTILINE_PALETTE,
  PERSPECTIVE_ORDER,
} from '../config/kpi';
import type {
  BscDataBundle,
  KpiStatus,
  LineStatusRow,
  RegistryRow,
  SnapshotRow,
  ValueRow,
} from '../types/bsc';
import { compareIsoDates, formatItalianDate } from '../lib/dates';
import { formatDisplayValue } from '../lib/formatters';
import { countLineStatuses, evaluateStatus, getOverallStatus } from '../lib/status';

export function buildHistory(bundle: BscDataBundle, kpiCode: string): ValueRow[] {
  return bundle.values
    .filter((row) => row.kpi_code === kpiCode)
    .sort((a, b) => {
      return (
        compareIsoDates(a.period, b.period) ||
        a.dimension_type.localeCompare(b.dimension_type, 'it') ||
        a.dimension_value.localeCompare(b.dimension_value, 'it') ||
        a.source_order - b.source_order
      );
    });
}

export function prepareChartData(details: ValueRow[], isMultiline: boolean): ValueRow[] {
  if (!isMultiline) return details;
  return details.filter(
    (row) => row.dimension_value.trim().toLocaleLowerCase('it-IT') !== 'totale',
  );
}

function latestRow(rows: ValueRow[]): ValueRow | null {
  if (rows.length === 0) return null;

  const latestPeriod = rows.reduce(
    (latest, row) => (row.period > latest ? row.period : latest),
    rows[0].period,
  );
  const candidates = rows.filter((row) => row.period === latestPeriod);
  const companyTotal = candidates.filter(
    (row) =>
      row.dimension_type.trim().toLocaleLowerCase('it-IT') === 'azienda' &&
      row.dimension_value.trim().toLocaleLowerCase('it-IT') === 'totale',
  );
  const pool = companyTotal.length > 0 ? companyTotal : candidates;

  return pool.reduce((chosen, row) =>
    row.source_order >= chosen.source_order ? row : chosen,
  );
}

export function getLatestLineStatuses(
  details: ValueRow[],
  registry: RegistryRow,
): LineStatusRow[] {
  const latestByDimension = new Map<string, ValueRow>();

  for (const row of details) {
    if (row.value === null) continue;
    const key = row.dimension_value;
    const existing = latestByDimension.get(key);
    if (
      !existing ||
      row.period > existing.period ||
      (row.period === existing.period && row.source_order >= existing.source_order)
    ) {
      latestByDimension.set(key, row);
    }
  }

  return [...latestByDimension.values()]
    .map((row): LineStatusRow => ({
      dimension_value: row.dimension_value,
      period: row.period,
      value: row.value as number,
      line_status: evaluateStatus({
        value: row.value,
        Target: registry.Target,
        Soglia_Minima: registry.Soglia_Minima,
        Soglia_Eccellenza: registry.Soglia_Eccellenza,
        Trend_Desiderato: registry.Trend_Desiderato,
      }),
    }))
    .sort(
      (a, b) =>
        a.line_status.localeCompare(b.line_status, 'it') ||
        a.dimension_value.localeCompare(b.dimension_value, 'it'),
    );
}

export function buildLineColorMap(details: ValueRow[]): Record<string, string> {
  const dimensions = [...new Set(details.map((row) => row.dimension_value))].sort((a, b) =>
    a.localeCompare(b, 'it'),
  );

  return Object.fromEntries(
    dimensions.map((dimension, index) => [
      dimension,
      MULTILINE_PALETTE[index % MULTILINE_PALETTE.length],
    ]),
  );
}

function buildEmptySnapshotRow(registry: RegistryRow): SnapshotRow {
  return {
    ...registry,
    period: null,
    value: null,
    dimension_type: null,
    dimension_value: null,
    source_file: null,
    note: null,
    record_type: null,
    Status: 'Manca dato',
    Valore_visualizzato: 'n.d.',
    Target_visualizzato: formatDisplayValue(registry.Target, registry.Unità_di_Misura),
    Ultimo_aggiornamento: 'n.d.',
    Ordinamento_Prospettiva: PERSPECTIVE_ORDER.indexOf(
      registry.Prospettiva_BSC as (typeof PERSPECTIVE_ORDER)[number],
    ),
  };
}

function snapshotFromStandardKpi(registry: RegistryRow, rows: ValueRow[]): SnapshotRow {
  const latest = latestRow(rows);
  if (!latest) return buildEmptySnapshotRow(registry);

  const status = evaluateStatus({
    value: latest.value,
    Target: registry.Target,
    Soglia_Minima: registry.Soglia_Minima,
    Soglia_Eccellenza: registry.Soglia_Eccellenza,
    Trend_Desiderato: registry.Trend_Desiderato,
  });

  return {
    ...registry,
    ...latest,
    Status: status,
    Valore_visualizzato: formatDisplayValue(latest.value, registry.Unità_di_Misura),
    Target_visualizzato: formatDisplayValue(registry.Target, registry.Unità_di_Misura),
    Ultimo_aggiornamento: formatItalianDate(latest.period),
    Ordinamento_Prospettiva: PERSPECTIVE_ORDER.indexOf(
      registry.Prospettiva_BSC as (typeof PERSPECTIVE_ORDER)[number],
    ),
  };
}

function snapshotFromMultilineKpi(registry: RegistryRow, rows: ValueRow[]): SnapshotRow {
  const chartRows = prepareChartData(rows, true);
  if (chartRows.length === 0) {
    return snapshotFromStandardKpi(registry, rows);
  }

  const lineStatuses = getLatestLineStatuses(chartRows, registry);
  const overall = getOverallStatus(lineStatuses.map((row) => row.line_status));
  const latest = latestRow(chartRows);

  if (!latest || !overall) {
    return buildEmptySnapshotRow(registry);
  }

  return {
    ...registry,
    period: latest.period,
    value: null,
    dimension_type: latest.dimension_type,
    dimension_value: 'multilinea',
    source_file: latest.source_file,
    note: latest.note,
    record_type: latest.record_type,
    Status: overall,
    Valore_visualizzato: 'n.a.',
    Target_visualizzato: formatDisplayValue(registry.Target, registry.Unità_di_Misura),
    Ultimo_aggiornamento: formatItalianDate(latest.period),
    Ordinamento_Prospettiva: PERSPECTIVE_ORDER.indexOf(
      registry.Prospettiva_BSC as (typeof PERSPECTIVE_ORDER)[number],
    ),
    multiline_status_counts: countLineStatuses(lineStatuses),
  };
}

export function buildLatestSnapshot(bundle: BscDataBundle): SnapshotRow[] {
  return bundle.registry
    .map((registry) => {
      const rows = buildHistory(bundle, registry.Codice);
      return MULTILINE_KPIS.has(registry.Codice)
        ? snapshotFromMultilineKpi(registry, rows)
        : snapshotFromStandardKpi(registry, rows);
    })
    .sort(
      (a, b) =>
        a.Ordinamento_Prospettiva - b.Ordinamento_Prospettiva ||
        a.Codice.localeCompare(b.Codice, 'it'),
    );
}

export function statusCounts(snapshot: SnapshotRow[]): Record<KpiStatus, number> {
  return snapshot.reduce<Record<KpiStatus, number>>(
    (counts, row) => {
      counts[row.Status] = (counts[row.Status] ?? 0) + 1;
      return counts;
    },
    {
      Eccellente: 0,
      'In target': 0,
      'Sotto target': 0,
      'Fuori target': 0,
      Critico: 0,
      'Manca dato': 0,
      'Da definire': 0,
      'No target': 0,
    },
  );
}
